import React from "react";
import { Button, Box } from "@showitinc/showit-wc";

import AppConstants from "../../../constants/AppConstants";
import ElementPropertiesAlignmentPanel from "./ElementPropertiesAlignmentPanel";
import ElementPropertiesClickActionsPanel from "./ElementPropertiesClickActionsPanel";
import ElementTypeProperties from "../../../components/ElementTypeProperties";
import UIActions from "../../../actions/UIActions";
import UserActions from "../../../actions/UserActions";
import { prepareMulticastUpdate, typesMatch, conjoinElements } from "../../../utils/MulticastUtils";
import { BUTTON, GRAPHIC, IFRAME, SOCIAL, VIDEO } from "../../../constants/DataTypes";
import PropertyPanelSectionWrapper from "../../base/PropertyPanelSectionWrapper";
import ElementPropertiesEffects from "../../../components/ElementPropertiesEffects";
import {
  checkSelectionForButtonElements,
  checkSelectionForFormFieldConversion,
  createButtonFromSelection,
} from "@/utils/PropertyUtils";
import ConvertToFormFieldButton from "./ConvertToFormFieldButton";
import useV2Feature from "@/hooks/useV2Feature";
import { FORMS_FLAG } from "@/constants/FeatureFlags.mjs";
import { TRACK_SOURCE_PANEL } from "@/constants/AnalyticsConstants.mjs";
import { FORM_FIELD_ELEMENT_TYPES, isFormFieldElementType } from "@/constants/FormFieldConstants.mjs";
import {
  addSelectionToGroup,
  canSelectionGroup,
  canRemoveFromGroup,
  canSelectionJoinGroup,
  removeSelectionFromGroup,
  groupSelection,
  parentHasAction,
} from "@/utils/GroupUtils";
import IDUtils from "@/utils/IDUtils";

/**
 * @typedef {import("@/types/Selection.mjs").ElementInfo} ElementInfo
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} Layouts
 * @typedef {import("immutable").Map<string, ElementInfo>} SelectedElements
 */

const { PANEL_SECTION_ELEMENT_EFFECTS } = AppConstants;

const ALIGNMENT = "alignment";
const CLICK_ACTIONS = "click-actions";
const ELEMENT_PROPERTIES = "element-properties";

const STYLE_EXCLUSION_TYPES = [BUTTON, GRAPHIC, IFRAME, SOCIAL, VIDEO];
const EFFECT_EXCLISION_TYPES = [BUTTON, ...FORM_FIELD_ELEMENT_TYPES];

/**
 *
 * @param {SelectedElements} selectedElements
 * @param {import("immutable").Map} block
 * @returns {boolean}
 */
const containerParentsHaveAction = (selectedElements, block) => {
  // parent path if elements are contained
  const containedBy = selectedElements.reduce(
    (result, { containedBy }) => (!result && containedBy ? containedBy : result),
    "",
  );

  if (containedBy) {
    const container = block.getIn(IDUtils.getArray(containedBy, 1));
    return parentHasAction({ parent: container });
  }

  return false;
};

/**
 * A component that dynamically renders property panels based on selected elements.
 *
 * @param {Object} props - The component props
 * @param {SelectedElements} props.selectedElements - An array of currently selected elements
 * @param {import("immutable").Map} props.block - The block data
 * @param {boolean} props.isWP - Whether the element is a WordPress element
 * @param {Layouts[number]} props.layout - The layout type
 * @param {Map} props.theme - The theme data
 * @param {Map} props.viewState - The view state data
 *
 * @returns {React.Component} A component that renders a collection of property panels
 *
 */
const ElementPropertiesWrapper = ({
  selectedElements,
  viewState,
  block,
  isWP,
  layout,
  theme,
  mappedElements,
}) => {
  const activeSection = viewState.get("activeSection");
  const onToggle = (sectionName) =>
    UIActions.toggleElementsPropertiesSection({ name: sectionName });

  const baseType = selectedElements.first()?.type;
  const match = typesMatch(selectedElements, block);
  const showPropertiesPanel = match && !STYLE_EXCLUSION_TYPES.includes(baseType);
  const showEffectsPanel = match && !EFFECT_EXCLISION_TYPES.includes(baseType);

  const conjoined = match ? conjoinElements(selectedElements, block, theme) : null;

  const conjoinedMappedElement =
    match && mappedElements && mappedElements.size > 0
      ? mappedElements.first().set("_allMappedElements", mappedElements)
      : null;

  const selectionIsPotentialButton = checkSelectionForButtonElements(selectedElements, block);
  const isFormsEnabled = useV2Feature(FORMS_FLAG);
  const selectionIsPotentialFormField =
    isFormsEnabled && checkSelectionForFormFieldConversion(selectedElements, block);
  const canJoinGroup = canSelectionJoinGroup(selectedElements);
  const canRemove = canRemoveFromGroup(selectedElements);
  const canGroup = canSelectionGroup(selectedElements);

  // are some elements locked
  const lock = selectedElements.some((e) => e.lock === "1");

  const contained = !!selectedElements.reduce(
    (result, { containedBy }) => (!result && containedBy ? containedBy : result),
    "",
  );
  const disableLinkPanel =
    containerParentsHaveAction(selectedElements, block) ||
    selectedElements.some(({ type }) => isFormFieldElementType(type));

  const onPropUpdate = prepareMulticastUpdate({
    elementInfos: selectedElements,
    preparedElementData: conjoined,
    layout,
    block,
    theme,
  });

  const groupButtonPadding =
    selectionIsPotentialButton || selectionIsPotentialFormField ? "1" : "4";
  const convertButtonPadding = canGroup || canJoinGroup || canRemove ? "1" : "3";

  return (
    <>
      {canGroup && (
        <Box pt="4" pb={groupButtonPadding} px="4">
          <Button
            fullWidth
            iconPrefix="group"
            iconSize="20"
            variant="minimal"
            onClick={() => groupSelection(selectedElements, block)}
          >
            Group Elements
          </Button>
        </Box>
      )}
      {canRemove && (
        <Box pt="4" pb={groupButtonPadding} px="4">
          <Button
            fullWidth
            iconPrefix="removeFromGroup"
            iconSize="20"
            variant="minimal"
            onClick={() => removeSelectionFromGroup(selectedElements, block)}
          >
            Remove from Group
          </Button>
        </Box>
      )}
      {canJoinGroup && (
        <Box pt="4" pb={groupButtonPadding} px="4">
          <Button
            fullWidth
            iconPrefix="addToGroup"
            iconSize="20"
            variant="minimal"
            onClick={() => addSelectionToGroup(selectedElements, block)}
          >
            Add to Group
          </Button>
        </Box>
      )}
      {selectionIsPotentialButton && (
        <Box pt={convertButtonPadding} pb="4" px="4">
          <Button
            fullWidth
            iconPrefix="convertButton"
            iconSize="20"
            variant="minimal"
            onClick={() => {
              UserActions.trackPostHogEvent("convert_to_button_clicked", {
                source: TRACK_SOURCE_PANEL,
              });
              createButtonFromSelection(selectedElements);
            }}
          >
            Convert to Button
          </Button>
        </Box>
      )}
      {selectionIsPotentialFormField && (
        <Box pt={selectionIsPotentialButton ? "1" : convertButtonPadding} pb="4" px="4">
          <ConvertToFormFieldButton selectedElements={selectedElements} />
        </Box>
      )}
      {showPropertiesPanel && (
        <ElementTypeProperties
          activeSection={activeSection}
          block={block}
          element={conjoined}
          preparedElement={conjoined}
          mappedElement={conjoinedMappedElement}
          key={ELEMENT_PROPERTIES}
          isLocked={lock}
          isWP={isWP}
          layout={layout}
          mixed
          onPropUpdate={onPropUpdate}
          onToggle={onToggle}
          theme={theme}
        />
      )}
      <ElementPropertiesAlignmentPanel
        contained={contained}
        key={ALIGNMENT}
        viewState={viewState}
      />

      {/* Todo: put at bottom of alignment panel */}
      {!disableLinkPanel && (
        <ElementPropertiesClickActionsPanel
          block={block}
          key={CLICK_ACTIONS}
          selectedElements={selectedElements}
          viewState={viewState}
        />
      )}
      {showEffectsPanel && (
        <PropertyPanelSectionWrapper
          title="Effects"
          open={activeSection === PANEL_SECTION_ELEMENT_EFFECTS}
          onSIPanelToggle={() => onToggle(PANEL_SECTION_ELEMENT_EFFECTS)}
        >
          <ElementPropertiesEffects
            isLocked={lock}
            element={conjoined}
            layout={layout}
            onPropUpdate={onPropUpdate}
            preparedElement={conjoined}
          />
        </PropertyPanelSectionWrapper>
      )}
    </>
  );
};

export default ElementPropertiesWrapper;
